// ─────────────────────────────────────────────
//  BoardAlertControls — Emergency + Urgency buttons for the OR board header.
//
//  Scope (per spec):
//    • PROVIDER (directSingle): fires for the room they're covering. One room →
//      the button sends DIRECTLY, no dropdown. (Covering >1 → a short menu of
//      just their rooms.)
//    • DOCTOR / AIC: a dropdown that lists THEIR covered rooms first ("My rooms")
//      then every other room ("Other rooms").
//
//  Alert routing:
//    • Emergency → the room's supervising doctor + the day's supervisors + AIC;
//      board goes red. Only the room's doctor may clear it.
//    • Urgency   → the room's doctor only (silent). Rooms with no doctor disabled.
//
//  Live blink (per spec):
//    • While a live emergency TARGETS me (I'm a recipient doctor/AIC), the
//      Emergency button BLINKS; it stops the moment the alert is cleared.
//    • While a live urgency targets me (the room's supervising doctor or its
//      CRNA — the people involved), the Urgency button BLINKS until cleared.
//    • A blinking 💬 "message sign" sits LEFT of Emergency whenever I have an
//      unread message; clicking it opens the messaging panel. Hidden at 0 unread.
//    (Blink reuses the global `atcFlash` keyframe — auto-disabled under
//     prefers-reduced-motion, see src/index.js.)
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useAlertCenter } from "../context/AlertContext";
import { useMessaging } from "../context/MessagingContext";
import { useToast } from "../context/ToastContext";
import { resolveRoomDoctorId, resolveUrgencyTargets, emergencyRoomStaff } from "../domain/alerts";
import { occupantRowForRoom, nowMinutes } from "../domain/roomOccupant";
import { unlockAudioOnFirstGesture } from "../audio/chime";
import MessagingPanel from "./MessagingPanel";
import UnreadBadge from "./UnreadBadge";
import tokens from "../theme/tokens";

const { color, radius, shadow, font, space } = tokens;

const sortRooms = (list) =>
  (list || [])
    .filter((r) => r?.name)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

export default function BoardAlertControls({ myRooms = [], rooms = [], assignments = [], compact = false, directSingle = false }) {
  const { raiseEmergency, raiseUrgency, myEmergencies, myUrgencies } = useAlertCenter();
  const { unread } = useMessaging();
  const toast = useToast();
  const [open, setOpen] = useState(null); // "emergency" | "urgency" | null
  const [busyId, setBusyId] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);

  // Escape closes an open room picker (keyboard parity with the backdrop
  // click — a menu the keyboard can open must be closable by keyboard).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Blink whenever a LIVE alert targets me; clears the instant it's resolved
  // (myEmergencies / myUrgencies drop the alert on resolve). Emergency blinks for
  // every recipient doctor/AIC; urgency only for the people involved (the room's
  // supervising doctor + its CRNA — the two ids urgency targets).
  const emergencyActive = (myEmergencies?.length || 0) > 0;
  const urgencyActive = (myUrgencies?.length || 0) > 0;
  const unreadCount = Number.isFinite(unread) ? unread : 0;

  // "My rooms" (covered) first, then every other targetable room (deduped).
  const mineList = sortRooms(myRooms);
  const mineIds = new Set(mineList.map((r) => r.id));
  const otherList = sortRooms(rooms).filter((r) => !mineIds.has(r.id));
  const targetable = [...mineList, ...otherList];
  // Exactly one targetable room → the click fires for THAT room directly, no
  // dropdown. This covers a provider on a single room (directSingle) AND any
  // viewer (incl. a previewed CRNA) whose only targetable room is their own —
  // so "click Urgency" always does something instead of opening a one-item menu
  // the user might not realise they must pick from. Multi-room viewers (doctors,
  // the AIC, a CRNA covering >1 room) still get the room picker.
  const direct =
    targetable.length === 1 && (directSingle || mineList.length === 1)
      ? targetable[0]
      : null;

  // ⚠️ THE ROW THE ROOM IS RUNNING NOW, not the first row Firestore happens to
  // return. A room with a morning case and an afternoon case would otherwise
  // send a 3 PM emergency to the CRNA who went home at noon, and leave the
  // person actually holding the patient off the broadcast. Resolved once per
  // send so every field of one alert names the same pair.
  const rowNow = (room) => occupantRowForRoom(room.name, assignments, nowMinutes());

  const sendEmergency = async (room) => {
    setBusyId(room.id);
    try {
      // The people in the room ride along on the alert so every supervisor's
      // phone can name them (MobileEmergencyAlarm) without its own listener.
      const row = rowNow(room);
      const staff = emergencyRoomStaff(room, row);
      const r = await raiseEmergency({
        roomId: room.id,
        roomName: room.name,
        doctorId: resolveRoomDoctorId(room, row),
        doctorName: staff.doctorName,
        crnaId: staff.crnaId,
        crnaName: staff.crnaName,
      });
      if (r.ok) toast.success(`🚨 Emergency for ${room.name} sent to ${r.targetCount}.`);
      else if (r.reason === "duplicate") toast.info(`An emergency is already active for ${room.name}.`);
      else if (r.reason === "no_targets") toast.error("No doctors or AIC on today's roster to alert.");
      else toast.error("Could not send the emergency. Call directly if critical.");
    } catch (e) {
      console.error("Board emergency failed:", e);
      toast.error("Could not send the emergency.");
    } finally {
      setBusyId(null);
      setOpen(null);
    }
  };

  const sendUrgency = async (room) => {
    const { doctorId, crnaId } = resolveUrgencyTargets(room, rowNow(room));
    if (!doctorId) { toast.error(`No doctor assigned to ${room.name} yet.`); return; }
    setBusyId(room.id);
    try {
      const r = await raiseUrgency({ roomId: room.id, roomName: room.name, doctorId, crnaId });
      if (r.ok) toast.success(`Urgent ping sent to ${room.name}'s doctor.`);
      else toast.error(`No doctor assigned to ${room.name} yet.`);
    } catch (e) {
      console.error("Board urgency failed:", e);
      toast.error("Could not send the urgent ping.");
    } finally {
      setBusyId(null);
      setOpen(null);
    }
  };

  const renderItems = (kind, list) => {
    const isEmergency = kind === "emergency";
    return list.map((room) => {
      // Same row the send will use, so the disabled state and the send can
      // never disagree about whether this room has a doctor.
      const noDoctor = !isEmergency && !resolveRoomDoctorId(room, rowNow(room));
      return (
        <button
          key={room.id}
          role="menuitem"
          disabled={noDoctor || busyId === room.id}
          style={{ ...s.menuItem, ...(noDoctor ? s.menuItemDisabled : {}) }}
          onClick={() => (isEmergency ? sendEmergency(room) : sendUrgency(room))}
          title={noDoctor ? "No doctor assigned to this room" : `Send ${kind} for ${room.name}`}
        >
          <span style={s.menuRoom}>{room.name}</span>
          <span style={s.menuMeta}>
            {isEmergency ? "→ doctors + AIC" : noDoctor ? "no doctor" : "→ room doctor"}
          </span>
        </button>
      );
    });
  };

  const renderMenu = (kind) => {
    const isEmergency = kind === "emergency";
    return (
      <div style={s.menu} role="menu" aria-label={`Pick a room to send ${kind}`}>
        {targetable.length === 0 ? (
          <div style={s.menuEmpty}>No rooms to alert.</div>
        ) : (
          <>
            {mineList.length > 0 && <div style={s.menuHead}>My rooms</div>}
            {renderItems(kind, mineList)}
            {otherList.length > 0 && (
              <>
                <div style={{ ...s.menuHead, marginTop: 4 }}>Other rooms</div>
                {renderItems(kind, otherList)}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // Click handler for a button: fire directly when a single covered room (no
  // dropdown for providers), else toggle the room menu.
  const onBtn = (kind) => {
    if (direct) {
      kind === "emergency" ? sendEmergency(direct) : sendUrgency(direct);
      return;
    }
    setOpen((v) => (v === kind ? null : kind));
  };
  const disabled = targetable.length === 0;
  const caret = direct ? "" : " ▾";

  return (
    <div style={s.wrap}>
      {/* Backdrop closes any open menu on outside click. */}
      {open && <div style={s.backdrop} onClick={() => setOpen(null)} aria-hidden="true" />}

      <div style={s.btnGroup}>
        {/* Message sign — LEFT of Emergency, only when I have unread messages.
            Blinks to catch the eye; opens the messaging panel on click. */}
        {unreadCount > 0 && (
          <div style={s.msgAnchor}>
            <button
              type="button"
              style={{ ...s.msgBtn, ...(compact ? s.compactBtn : {}), ...s.blink }}
              aria-label={`New message — ${unreadCount} unread`}
              title="New message"
              onClick={() => { unlockAudioOnFirstGesture(); setMsgOpen(true); }}
            >
              💬{compact ? "" : " Message"}
            </button>
            <span style={s.msgBadge} aria-hidden="true">
              <UnreadBadge count={unreadCount} />
            </span>
          </div>
        )}

        <div style={s.anchor}>
          <button
            style={{ ...s.emBtn, ...(compact ? s.compactBtn : {}), ...(disabled ? s.btnDisabled : {}), ...(emergencyActive ? s.blink : {}) }}
            aria-haspopup={direct ? undefined : "menu"}
            aria-expanded={open === "emergency"}
            disabled={disabled || busyId != null}
            title={direct ? `Send emergency for ${direct.name}` : "Send an emergency"}
            onClick={() => onBtn("emergency")}
          >
            🚨{compact ? "" : " Emergency"}{caret}
          </button>
          {!direct && open === "emergency" && renderMenu("emergency")}
        </div>

        <div style={s.anchor}>
          <button
            style={{ ...s.urBtn, ...(compact ? s.compactBtn : {}), ...(disabled ? s.btnDisabled : {}), ...(urgencyActive ? s.blink : {}) }}
            aria-haspopup={direct ? undefined : "menu"}
            aria-expanded={open === "urgency"}
            disabled={disabled || busyId != null}
            title={direct ? `Send urgent ping for ${direct.name}` : "Send an urgent ping"}
            onClick={() => onBtn("urgency")}
          >
            ⚡{compact ? "" : " Urgency"}{caret}
          </button>
          {!direct && open === "urgency" && renderMenu("urgency")}
        </div>
      </div>

      {/* Messaging panel opened by the message sign (reuses the topbar panel). */}
      <MessagingPanel isOpen={msgOpen} onClose={() => setMsgOpen(false)} />
    </div>
  );
}

const s = {
  wrap: { position: "relative" },
  btnGroup: { display: "flex", gap: space.sm, position: "relative", zIndex: 50 },
  anchor: { position: "relative" },
  emBtn: {
    background: color.accent.error, color: "#fff", border: "none", borderRadius: radius.sm,
    padding: "9px 14px", fontSize: font.size.body, fontWeight: font.weight.bold, cursor: "pointer", whiteSpace: "nowrap",
  },
  urBtn: {
    background: color.accent.closing, color: "#fff", border: "none", borderRadius: radius.sm,
    padding: "9px 14px", fontSize: font.size.body, fontWeight: font.weight.bold, cursor: "pointer", whiteSpace: "nowrap",
  },
  compactBtn: { padding: "5px 8px", fontSize: font.size.label },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  // Blink for a live alert that targets me / a new message. Reuses the global
  // atcFlash keyframe (index.js), which is disabled under prefers-reduced-motion.
  blink: { animation: "atcFlash 1s steps(1) infinite" },
  msgAnchor: { position: "relative", display: "inline-flex" },
  msgBtn: {
    background: color.surface.raised, color: color.text.secondary,
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    padding: "9px 14px", fontSize: font.size.body, fontWeight: font.weight.bold,
    cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit",
  },
  msgBadge: { position: "absolute", top: -6, right: -6, pointerEvents: "none" },
  backdrop: { position: "fixed", inset: 0, zIndex: 40 },
  menu: {
    position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50,
    background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.lg,
    borderRadius: radius.md, padding: 6, minWidth: 240, maxHeight: 360, overflowY: "auto",
  },
  menuHead: {
    fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.muted,
    textTransform: "uppercase", letterSpacing: ".5px", padding: "6px 10px 8px",
  },
  menuEmpty: { fontSize: font.size.label, color: color.text.muted, padding: "8px 10px" },
  menuItem: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.md, width: "100%",
    background: "transparent", border: "none", borderRadius: radius.sm, padding: "9px 10px",
    cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: color.text.primary,
  },
  menuItemDisabled: { opacity: 0.45, cursor: "not-allowed" },
  menuRoom: { fontSize: font.size.body, fontWeight: font.weight.bold },
  menuMeta: { fontSize: font.size.label, color: color.text.muted, whiteSpace: "nowrap" },
};
